import React, {useState} from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import Modal from 'components/Modals/Modal';
import ButtonPrimary from 'components/buttons/ButtonPrimary/styles';
import {convertOrderToLocationDeliveryApi} from 'helpers/services/api/ordersV2';
import {ORDER} from 'helpers/constants/orders_v2';
import {
  HYBRID_HOLD_SECONDS,
  hybridHoldExpiresAt
} from 'helpers/functionUtils/hybridDelivery';
import useCountdown from 'routes/App/ordersV2/OrderDetails/DriverOptOut/useCountdown';
import {
  DriverOptOutActions,
  DriverOptOutBarFill,
  DriverOptOutBarTrack,
  DriverOptOutModalRow,
  DriverOptOutModalText,
  DriverOptOutModalTitle,
  DriverOptOutModalValue,
  DriverOptOutPrimaryButton,
  DriverOptOutSecondaryButton
} from 'routes/App/ordersV2/OrderDetails/DriverOptOut/styles';

// Last stretch of the window, where the merchant is warned in red.
const URGENT_SECONDS = 15;

const DriverOptOutAction = ({order, onSuccess, className, canSwitch}) => {
  const [isConfirmOpen, setConfirmOpen] = useState(false);
  const [isErrorOpen, setErrorOpen] = useState(false);
  const [isSwitching, setSwitching] = useState(false);
  const {seconds, label} = useCountdown(hybridHoldExpiresAt(order));

  const isUrgent = canSwitch && seconds > 0 && seconds <= URGENT_SECONDS;
  const remainingPercent = canSwitch
    ? Math.round((seconds / HYBRID_HOLD_SECONDS) * 100)
    : 0;

  const confirm = async () => {
    setSwitching(true);
    try {
      const response = await convertOrderToLocationDeliveryApi(order[ORDER.ID]);
      // request() resolves to false instead of throwing when the network is down.
      if (!response) throw new Error('Could not switch to own driver');
      setConfirmOpen(false);
      onSuccess(response.data);
    } catch {
      setConfirmOpen(false);
      setErrorOpen(true);
    } finally {
      setSwitching(false);
    }
  };

  return (
    <>
      {canSwitch && (
        <ButtonPrimary
          className={className}
          onClick={(event) => {
            event.stopPropagation();
            setConfirmOpen(true);
          }}
        >
          <span>My driver</span>
        </ButtonPrimary>
      )}

      <span role='presentation' onClick={(event) => event.stopPropagation()}>
        {isConfirmOpen && (
          <Modal
            classParameter='DialogCompact'
            onCloseAction={() => setConfirmOpen(false)}
            title={
              <DriverOptOutModalTitle>
                {canSwitch ? 'Use your own driver?' : 'Sauce driver confirmed'}
              </DriverOptOutModalTitle>
            }
            body={
              <>
                <DriverOptOutModalRow>
                  <DriverOptOutModalText>
                    {canSwitch
                      ? 'A Sauce driver is already assigned. You can hand this delivery to your own staff before the timer runs out.'
                      : 'The opt-out window has closed. This delivery is running with a Sauce driver.'}
                  </DriverOptOutModalText>
                  <DriverOptOutModalValue
                    className={cn({urgent: isUrgent, locked: !canSwitch})}
                  >
                    {canSwitch ? label : 'Locked'}
                  </DriverOptOutModalValue>
                </DriverOptOutModalRow>
                <DriverOptOutBarTrack>
                  <DriverOptOutBarFill
                    className={cn({urgent: isUrgent})}
                    style={{width: `${remainingPercent}%`}}
                  />
                </DriverOptOutBarTrack>
                <DriverOptOutActions>
                  {canSwitch ? (
                    <>
                      <DriverOptOutSecondaryButton
                        className='ButtonMedium'
                        onClick={() => setConfirmOpen(false)}
                      >
                        <span>No</span>
                      </DriverOptOutSecondaryButton>
                      <DriverOptOutPrimaryButton
                        className='ButtonMedium solid'
                        disabled={isSwitching}
                        onClick={confirm}
                      >
                        <span>Yes</span>
                      </DriverOptOutPrimaryButton>
                    </>
                  ) : (
                    <DriverOptOutSecondaryButton
                      className='ButtonMedium'
                      onClick={() => setConfirmOpen(false)}
                    >
                      <span>Close</span>
                    </DriverOptOutSecondaryButton>
                  )}
                </DriverOptOutActions>
              </>
            }
          />
        )}

        {isErrorOpen && (
          <Modal
            classParameter='DialogCompact'
            onCloseAction={() => setErrorOpen(false)}
            title={
              <DriverOptOutModalTitle>
                Couldn&apos;t switch to your own driver
              </DriverOptOutModalTitle>
            }
            body={
              <>
                <DriverOptOutModalText>
                  Something went wrong on our end. A Sauce driver is still
                  assigned to this order. Try to cancel the Sauce driver or call
                  support to help you.
                </DriverOptOutModalText>
                <DriverOptOutActions>
                  <DriverOptOutPrimaryButton
                    className='ButtonMedium solid'
                    onClick={() => setErrorOpen(false)}
                  >
                    <span>Got it</span>
                  </DriverOptOutPrimaryButton>
                </DriverOptOutActions>
              </>
            }
          />
        )}
      </span>
    </>
  );
};

DriverOptOutAction.propTypes = {
  order: PropTypes.shape({}).isRequired,
  onSuccess: PropTypes.func,
  className: PropTypes.string,
  canSwitch: PropTypes.bool
};

DriverOptOutAction.defaultProps = {
  onSuccess: () => {},
  className: 'ButtonSmall solid',
  canSwitch: true
};

export default DriverOptOutAction;
